import { atom, getDefaultStore, useAtomValue } from "jotai";

/**
 * Thread ids with a server-pushed canvas update the user hasn't looked at yet.
 * The `thread_canvas:update` socket handler marks a thread unseen; the thread
 * view badges the canvas toggle icon and clears the flag when the pane is
 * opened. Purely client-side and non-persisted — it never reveals the pane
 * (revealing stays user-owned), it only draws attention.
 *
 * A single jotai atom holds the set (mirroring `threadLastVisitedAtAtom`), so
 * the socket handler can mark a thread from outside React via the default store
 * while the thread view reads it reactively.
 */
const unseenCanvasThreadsAtom = atom<ReadonlySet<number>>(new Set<number>());

/** Flag a thread's canvas toggle for attention after a server push.
 * See the atom above for the full rationale.
 */
export function markCanvasUpdateUnseen(threadId: number): void {
  const store = getDefaultStore();
  const prev = store.get(unseenCanvasThreadsAtom);
  if (prev.has(threadId)) return;
  store.set(unseenCanvasThreadsAtom, new Set(prev).add(threadId));
}

/** Drop a thread's unseen flag once the user opens / looks at the canvas, so the
 *  toggle stops badging. Paired with {@link markCanvasUpdateUnseen}. */
export function clearCanvasUpdateUnseen(threadId: number): void {
  const store = getDefaultStore();
  const prev = store.get(unseenCanvasThreadsAtom);
  if (!prev.has(threadId)) return;
  const next = new Set(prev);
  next.delete(threadId);
  store.set(unseenCanvasThreadsAtom, next);
}

/** Reactively read a thread's unseen-canvas flag so the thread view can badge
 *  (and only badge) the canvas toggle. */
export function useCanvasUpdateUnseen(threadId: number): boolean {
  return useAtomValue(unseenCanvasThreadsAtom).has(threadId);
}
